import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "./vfs_fonts";
import moment from "moment";
import { formatNumber } from "../../../utils/formatNumber";
import "moment/locale/th";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
  THSarabunNew: {
    normal: "THSarabunNew.ttf",
    bold: "THSarabunNew.ttf",
    italics: "THSarabunNew.ttf",
    bolditalics: "THSarabunNew.ttf",
  },
};

const N = (value) => Number(value ?? 0) || 0;

const getDestinationEntries = (item) =>
  Array.isArray(item?.destinationEntries)
    ? item.destinationEntries.filter((entry) => entry && N(entry.weight) >= 0)
    : [];

const getInvoiceSummary = (item) => {
  const destinationEntries = getDestinationEntries(item);
  if (destinationEntries.length === 0) {
    return {
      netWeight: N(item.netWeight),
      pricePerUnit: N(item.pricePerUnit),
      total: N(item.netPrice),
    };
  }

  const netWeight = destinationEntries.reduce(
    (sum, entry) => sum + N(entry.weight),
    0
  );
  const total = destinationEntries.reduce(
    (sum, entry) => sum + N(entry.weight) * N(entry.pricePerUnit),
    0
  );

  return {
    netWeight,
    pricePerUnit: netWeight > 0 ? total / netWeight : 0,
    total,
  };
};

const buildInvoiceTableRows = (items) =>
  items.flatMap((item) => {
    const destinationEntries = getDestinationEntries(item);
    const summary = getInvoiceSummary(item);
    const usesDestination = destinationEntries.length > 0;
    const mainRow = [
      item.productName || "-",
      "กก.",
      usesDestination ? "-" : formatNumber(item.weight),
      usesDestination ? "-" : formatNumber(item.deductWeight),
      formatNumber(summary.netWeight),
      formatNumber(summary.pricePerUnit),
      formatNumber(summary.total),
    ];

    if (destinationEntries.length <= 1) return [mainRow];

    const destinationRows = destinationEntries.map((entry, index) => [
      {
        text: `- รายการที่ ${index + 1} ${entry.name || "-"}`,
        margin: [10, 0, 0, 0],
      },
      "",
      "-",
      "-",
      formatNumber(entry.weight),
      formatNumber(entry.pricePerUnit),
      formatNumber(N(entry.weight) * N(entry.pricePerUnit)),
    ]);

    return [mainRow, ...destinationRows];
  });

const hasDestinationData = (items) =>
  items.some((item) => getDestinationEntries(item).length > 0);

const getDestinationTotal = (items) =>
  items.reduce((sum, item) => sum + getInvoiceSummary(item).total, 0);

export function generateInvoicePdfBlob(transaction) {
  const N = (v) => Number(v ?? 0) || 0;

  const finalTicketTotalRaw = N(transaction?.totalNetPrice);
  const items = Array.isArray(transaction?.items) ? transaction?.items : [];

  const adjustedRows = adjustItemPricesByFinalTotal(items, finalTicketTotalRaw);

  const finalTicketTotal = hasDestinationData(items)
    ? getDestinationTotal(items)
    : adjustedRows.reduce((s, r) => s + N(r.total), 0);
  const invoiceTableRows = buildInvoiceTableRows(items);

  const VAT_RATE = 0.07;
  const taxOption = transaction?.customer?.salesTaxOption; // 'vatExcluded' | 'vatIncluded' | อื่นๆ = ไม่มี VAT

  // baseAmountRaw คือยอดก่อน VAT
  // - ถ้าราคารวมเป็นแบบ exclude VAT => grand = base + vat
  // - ถ้า include VAT => แยก vat ออก
  // - ถ้าไม่มี VAT => beforeVat = grand = baseAmountRaw
  const baseAmountRaw = finalTicketTotal;

  let beforeVat, vatAmt, grand;
  if (taxOption === "vatExcluded") {
    beforeVat = baseAmountRaw;
    vatAmt = beforeVat * VAT_RATE;
    grand = beforeVat + vatAmt;
  } else if (taxOption === "vatIncluded") {
    grand = baseAmountRaw;
    vatAmt = (grand * VAT_RATE) / (1 + VAT_RATE);
    beforeVat = grand - vatAmt;
  } else {
    beforeVat = baseAmountRaw;
    vatAmt = 0;
    grand = beforeVat;
  }

  function adjustItemPricesByFinalTotal(items = [], finalTotal = 0) {
    const N = (v) => Number(v ?? 0) || 0;

    // 1) เตรียมข้อมูล
    const rows = items.map((it) => ({
      productName: it.productName,
      weight: N(it.netWeight),
      total: N(it.netPrice),
    }));

    // 2) รวมยอดทั้งหมดเดิม
    const sumOriginal = rows.reduce((sum, r) => sum + r.total, 0);
    const target = N(finalTotal);

    if (sumOriginal === 0 || target === 0) {
      // ถ้าไม่มียอดหรือยอดรวมเป็น 0 → คืนค่าเดิมไป
      return rows.map((r) => ({
        productName: r.productName,
        weight: r.weight,
        pricePerUnit: r.weight > 0 ? r.total / r.weight : 0,
        total: r.total,
      }));
    }

    // 3) คำนวณอัตราส่วนระหว่าง target กับยอดรวมเดิม
    const ratio = target / sumOriginal;

    // 4) ปรับราคาใหม่ตามสัดส่วน
    const adjustedRows = rows.map((r) => {
      const newTotal = r.total * ratio;
      const newPPU = r.weight > 0 ? newTotal / r.weight : 0;
      return {
        productName: r.productName,
        weight: r.weight,
        pricePerUnit: newPPU,
        total: newTotal,
      };
    });

    return adjustedRows;
  }

  return new Promise((resolve) => {
    const totalPrice = transaction.items.reduce(
      (sum, item) => sum + (item.netPrice || 0),
      0
    );

    const docDefinition = {
      content: [
        { text: "ใบแจ้งหนี้ (Invoice)", style: "header" },

        {
          text: `${transaction?.branch?.companyName || ""}\n${transaction?.branch?.address || ""}
  โทร: ${transaction?.branch?.phoneNumber || ""}\nเปิด: จันทร์ - เสาร์ เวลา 8.00 - 16.00 น. (ปิดทุกวันอาทิตย์)`,
          margin: [0, 10, 0, 10],
        },

        {
          columns: [
            [
              {
                text: `ชื่อขนส่งสินค้า: ${transaction.shipping.shippingName || "-"}`,
              },
              { text: `เลขที่ตั๋ว: ${transaction.billNumber || "-"}` },
              { text: `เลขที่ใบสั่งซื้อ: ${transaction.orderNumber || "-"}` },
              {
                text: `ส่งไปที่:\n[C${String(transaction.customerId || "-").padStart(3, "0")}] ${
                  transaction.customerName || "-"
                }
${transaction.customer?.address || ""}
${transaction.customer?.phone || ""}`,
              },
            ],
            [
              {
                text: transaction.createdAt
                  ? (() => {
                      const date = moment(transaction.createdAt).locale("th");
                      const buddhistYear = date.year() + 543;
                      return `วันที่เปิดตั๋ว: ${date.format("DD/MM/")}${buddhistYear} ${date.format("HH:mm")}`;
                    })()
                  : "วันที่เปิดตั๋ว: -",
              },
              {
                text: transaction.shipping && transaction.shipping.createdAt
                    ? (() => {
                        const date = moment(
                          transaction.shipping.createdAt
                        ).locale("th");
                        const buddhistYear = date.year() + 543;
                        return `วันที่ขาย: ${date.format("DD/MM/")}${buddhistYear} ${date.format("HH:mm")}`;
                      })()
                    : "วันที่ขาย: -",
              },
              {
                text: `เลขตู้ขนส่ง: ${(transaction.shipping && transaction.shipping.transportBoxNumber) || "-"}`,
              },
              {
                text: `ชื่อคนขับ: ${(transaction.shipping && transaction.shipping.driverName) || "-"}`,
              },
              // {
              //   text: `รถขนส่ง: ${transaction.car.type || "-"} ${transaction.car.license || "-"}`,
              // },
              {
                text: transaction.shipping?.phoneNumber
                  ? `โทรศัพท์ ${transaction.shipping.phoneNumber.replace(/^(\d{3})(\d{7})$/, "$1-$2")}`
                  : "โทรศัพท์: -",
              },
            ],
          ],
          margin: [0, 0, 0, 10],
        },

        {
          table: {
            widths: ["*", "auto", "auto", "auto", "auto", "auto", "auto"],
            body: [
              [
                { text: "รายการ", bold: true },
                { text: "ประเภท", bold: true },
                { text: "รวม", bold: true },
                { text: "หัก", bold: true },
                { text: "สุทธิ", bold: true },
                { text: "ราคาต่อหน่วย", bold: true },
                { text: "ราคาบาท", bold: true },
              ],
              ...invoiceTableRows,
            ],
          },
          layout: "lightHorizontalLines",
          margin: [0, 10, 0, 10],
        },
        {
          text: `เปิดตั๋วโดย ${transaction.openByNames || "-"}`,
          margin: [0, 10, 0, 10],
        },
        {
          table: {
            widths: ["*", "auto"],
            body: [
              [
                "ยอดรวมก่อนเสียภาษี",
                { text: formatNumber(beforeVat), alignment: "right" },
              ],
              [
                "ภาษีมูลค่าเพิ่ม (VAT 7%)",
                { text: formatNumber(vatAmt), alignment: "right" },
              ],
              [
                "ยอดรวม",
                {
                  text: formatNumber(grand),
                  alignment: "right",
                  bold: true,
                },
              ],
            ],
          },
          layout: "noBorders",
          margin: [0, 10, 0, 30],
        },

        {
          text: "ลงชื่อ: ____________________________",
          alignment: "right",
        },
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          alignment: "center",
          margin: [0, 0, 0, 10],
        },
      },
      defaultStyle: {
        font: "THSarabunNew",
        fontSize: 16,
      },
    };

    const pdfDoc = pdfMake.createPdf(docDefinition);
    pdfDoc.getBlob((blob) => resolve(blob));
  });
}

export function generateInvoicePdf(transaction) {
  const N = (v) => Number(v ?? 0) || 0;

  const finalTicketTotalRaw = N(transaction?.totalNetPrice);
  const items = Array.isArray(transaction?.items) ? transaction?.items : [];

  const adjustedRows = adjustItemPricesByFinalTotal(items, finalTicketTotalRaw);

  const finalTicketTotal = hasDestinationData(items)
    ? getDestinationTotal(items)
    : adjustedRows.reduce((s, r) => s + N(r.total), 0);
  const invoiceTableRows = buildInvoiceTableRows(items);

  const VAT_RATE = 0.07;
  const taxOption = transaction?.customer?.salesTaxOption; // 'vatExcluded' | 'vatIncluded' | อื่นๆ = ไม่มี VAT

  // baseAmountRaw คือยอดก่อน VAT
  // - ถ้าราคารวมเป็นแบบ exclude VAT => grand = base + vat
  // - ถ้า include VAT => แยก vat ออก
  // - ถ้าไม่มี VAT => beforeVat = grand = baseAmountRaw
  const baseAmountRaw = finalTicketTotal;

  let beforeVat, vatAmt, grand;
  if (taxOption === "vatExcluded") {
    beforeVat = baseAmountRaw;
    vatAmt = beforeVat * VAT_RATE;
    grand = beforeVat + vatAmt;
  } else if (taxOption === "vatIncluded") {
    grand = baseAmountRaw;
    vatAmt = (grand * VAT_RATE) / (1 + VAT_RATE);
    beforeVat = grand - vatAmt;
  } else {
    beforeVat = baseAmountRaw;
    vatAmt = 0;
    grand = beforeVat;
  }


  function adjustItemPricesByFinalTotal(items = [], finalTotal = 0) {
    const N = (v) => Number(v ?? 0) || 0;

    // 1) เตรียมข้อมูล
    const rows = items.map((it) => ({
      productName: it.productName,
      weight: N(it.netWeight),
      total: N(it.netPrice),
    }));

    // 2) รวมยอดทั้งหมดเดิม
    const sumOriginal = rows.reduce((sum, r) => sum + r.total, 0);
    const target = N(finalTotal);

    if (sumOriginal === 0 || target === 0) {
      // ถ้าไม่มียอดหรือยอดรวมเป็น 0 → คืนค่าเดิมไป
      return rows.map((r) => ({
        productName: r.productName,
        weight: r.weight,
        pricePerUnit: r.weight > 0 ? r.total / r.weight : 0,
        total: r.total,
      }));
    }

    // 3) คำนวณอัตราส่วนระหว่าง target กับยอดรวมเดิม
    const ratio = target / sumOriginal;

    // 4) ปรับราคาใหม่ตามสัดส่วน
    const adjustedRows = rows.map((r) => {
      const newTotal = r.total * ratio;
      const newPPU = r.weight > 0 ? newTotal / r.weight : 0;
      return {
        productName: r.productName,
        weight: r.weight,
        pricePerUnit: newPPU,
        total: newTotal,
      };
    });

    return adjustedRows;
  }
  // รวมราคาสินค้าในรายการ
  const totalPrice = transaction.items.reduce(
    (sum, item) => sum + (item.netPrice || 0),
    0
  );

  const docDefinition = {
    content: [
      { text: "ใบแจ้งหนี้ (Invoice)", style: "header" },

      {
        text: `${transaction?.branch?.companyName || ""}\n${transaction?.branch?.address || ""}
โทร: ${transaction?.branch?.phoneNumber || ""}\nเปิด: จันทร์ - เสาร์ เวลา 8.00 - 16.00 น. (ปิดทุกวันอาทิตย์)`,
        margin: [0, 10, 0, 10],
      },

      {
        columns: [
          [
            {
              text: `ชื่อขนส่งสินค้า: ${transaction.shipping.shippingName || "-"}`,
            },
            { text: `เลขที่ตั๋ว: ${transaction.billNumber || "-"}` },
            { text: `เลขที่ใบสั่งซื้อ: ${transaction.orderNumber || "-"}` },
            {
              text: `ส่งไปที่:\n[C${String(transaction.customerId || "-").padStart(3, "0")}] ${
                transaction.customerName || "-"
              }
${transaction.customer?.address || ""}
${transaction.customer?.phone || ""}`,
            },
          ],
          [
            {
              text: transaction.createdAt
                ? (() => {
                    const date = moment(transaction.createdAt).locale("th");
                    const buddhistYear = date.year() + 543;
                    return `วันที่เปิดตั๋ว: ${date.format("DD/MM/")}${buddhistYear} ${date.format("HH:mm")}`;
                  })()
                : "วันที่เปิดตั๋ว: -",
            },
            {
              text: transaction.shipping.createdAt
                ? (() => {
                    const date = moment(transaction.shipping.createdAt).locale(
                      "th"
                    );
                    const buddhistYear = date.year() + 543;
                    return `วันที่ขาย: ${date.format("DD/MM/")}${buddhistYear} ${date.format("HH:mm")}`;
                  })()
                : "วันที่ขาย: -",
            },
            {
              text: `เลขตู้ขนส่ง: ${transaction.shipping.transportBoxNumber || "-"}`,
            },
            { text: `ชื่อคนขับ: ${transaction.shipping.driverName || "-"}` },
            // {
            //   text: `รถขนส่ง: ${transaction.car.type || "-"} ${transaction.car.license || "-"}`,
            // },
            {
              text: transaction.shipping?.phoneNumber
                ? `โทรศัพท์ ${transaction.shipping.phoneNumber.replace(/^(\d{3})(\d{7})$/, "$1-$2")}`
                : "โทรศัพท์: -",
            },
          ],
        ],
        margin: [0, 0, 0, 10],
      },

      {
        table: {
          widths: ["*", "auto", "auto", "auto", "auto", "auto", "auto"],
          body: [
            [
              { text: "รายการ", bold: true },
              { text: "ประเภท", bold: true },
              { text: "รวม", bold: true },
              { text: "หัก", bold: true },
              { text: "สุทธิ", bold: true },
              { text: "ราคาต่อหน่วย", bold: true },
              { text: "ราคารวม", bold: true },
            ],
            ...invoiceTableRows,
          ],
        },
        layout: "lightHorizontalLines",
        margin: [0, 10, 0, 10],
      },

      {
        text: `เปิดตั๋วโดย ${transaction.openByNames || "-"}`,
        margin: [0, 0, 0, 20],
      },

      {
        table: {
          widths: ["*", "auto"],
          body: [
            [
              "ยอดรวมก่อนเสียภาษี",
            { text: formatNumber(beforeVat), alignment: "right" },
            ],
            ["ภาษีมูลค่าเพิ่ม (VAT 7%)", { text: formatNumber(vatAmt), alignment: "right" }],
            [
              "ยอดรวม",
              {
                text: formatNumber(grand),
                alignment: "right",
                bold: true,
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 10, 0, 30],
      },

      {
        text: "ลงชื่อ: ____________________________",
        alignment: "right",
      },
    ],
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 10],
      },
    },
    defaultStyle: {
      font: "THSarabunNew",
      fontSize: 16,
    },
  };

  pdfMake.createPdf(docDefinition).open();
}
